import React, { useRef, useCallback, useEffect, useState } from 'react'
import {
  Button,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'

import {
  Close as CloseIcon
} from '@mui/icons-material'

import Webcam from 'react-webcam'
import { useTranslation } from 'react-i18next'
import { defaultFunction } from "../../utilities/utilities"
import { connect } from 'react-redux'
import { ID_CAMERA_PHOTO } from '../../../variables'



function PhotoDialog(props) {
  const {
    open = false,
    setPhoto = defaultFunction,
    onClose = defaultFunction,
    photo = '',
    cameras = []
  } = props

  const { t } = useTranslation()
  const [camera, setCamera] = useState('')
  const [localCameras, setLocalCameras] = useState([])
  const [cameraBlocked, setCameraBlocked] = useState(false)

  const canEnumerateCameras = typeof navigator.mediaDevices?.enumerateDevices === 'function'

  const webcamRef = useRef(null)
  const capture = useCallback(
    () => {
      setPhoto(webcamRef.current.getScreenshot())
    },
    [setPhoto]
  )

  const handleCameraChange = (event) => {
    localStorage.setItem(ID_CAMERA_PHOTO, event.target.value)
    setCamera(event.target.value)
  }

  useEffect(() => {
    if (!open) return;
    if (!canEnumerateCameras) { setCameraBlocked(true); return; }

    const enumerateLocalCameras = () => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    };

    enumerateLocalCameras();

    navigator.mediaDevices.addEventListener('devicechange', enumerateLocalCameras);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateLocalCameras);
    };
  }, [open, canEnumerateCameras]);

  useEffect(() => {
    if (cameras.length > 0) setLocalCameras(cameras);
  }, [cameras]);

  // Poll while the list is empty or all labels are blank (permission not yet granted).
  // Self-stops once real labels are available — no need to close and reopen the dialog.
  useEffect(() => {
    if (!open || !canEnumerateCameras) return;
    if (localCameras.length > 0 && localCameras.some(c => c.label)) return;

    const timer = setTimeout(() => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    }, 1500);

    return () => clearTimeout(timer);
  }, [open, canEnumerateCameras, localCameras]);

  useEffect(() => {
    if (!open || localCameras.length === 0) return;
    const stored = localStorage.getItem(ID_CAMERA_PHOTO);
    const isValid = localCameras.some(c => c.deviceId === stored);
    setCamera(isValid ? stored : localCameras[0].deviceId);
  }, [open, localCameras]);

  return (
    <Dialog
      className='visitor-dialog'
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="sm">
      <DialogContent>
        <Stack gap={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <h1>{t("Capture photo")}</h1>
            <IconButton
              aria-label="close"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack gap={2} alignItems="center">
            {cameraBlocked ? (
              <p style={{ color: 'var(--error, #d32f2f)', textAlign: 'center' }}>
                {t('Camera access requires a secure connection (HTTPS). Please contact your administrator.')}
              </p>
            ) : photo ? (
              <Stack gap={2} alignItems="center">
                <div className="webcam-wrapper">
                  <img src={photo} alt={t("user")} />
                </div>
                <h2>{t("Place your face in the white circle.")}</h2>
                <Stack gap={2} direction="row" alignItems="center" justifyContent="space-between">
                  <Button
                    variant='outlined'
                    size='large'
                    onClick={() => setPhoto()}
                  >
                    {t("Capture again")}
                  </Button>
                  <Button
                    variant='contained'
                    size='large'
                    onClick={onClose}
                  >
                    {t("Save")}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="select-camera">{t('Camera')}</InputLabel>
                  <Select
                    labelId="select-camera"
                    id="demo-simple-select"
                    value={camera}
                    label={t("Camera")}
                    size='small'
                    onChange={handleCameraChange}
                  >
                    {localCameras.map((cam) => <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <div className='webcam-wrapper'>
                  <Webcam
                    key={camera}
                    ref={webcamRef}
                    audio={false}
                    width={370}
                    height={370}
                    screenshotFormat="image/jpeg"
                    videoConstraints={camera ? { deviceId: { exact: camera } } : undefined}
                  />
                </div>
                <Stack gap={2}>
                  <h2>{t("Place your face in the white circle.")}</h2>
                  <Button variant='contained' size='large' onClick={capture}>
                    {t("Capture")}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

const mapStateToProps = state => {
  return {
    cameras: state.utilities.cameras
  }
}

export default connect(mapStateToProps)(PhotoDialog)